import { Link } from "react-router-dom";
import LegalDocumentPage from "@/components/legal/LegalDocumentPage";
import {
  RADAR_LEGAL_ENTITY,
  RADAR_JURISDICTION,
  ROMANIA_DPA_FULL_NAME,
  ROMANIA_DPA_SHORT,
  ROMANIA_DPA_WEBSITE,
  ROMANIA_GDPR_IMPLEMENTING_LAW,
  COMPANY_REGISTERED_OFFICE,
  COMPANY_TRADE_REGISTER_ID,
  COMPANY_CUI,
  hasPublicCompanyRegistryDetails,
} from "@/lib/legalEntity";

export default function PrivacyPolicyPage() {
  return (
    <LegalDocumentPage
      title="Privacy Policy"
      description="Privacy policy for Radar (S.C. Aquivo Labs SRL, Romania): GDPR, Law 190/2018, ANSPDCP, and how we handle your data."
      canonical="/privacy"
    >
      <p>
        This Privacy Policy describes how {RADAR_LEGAL_ENTITY} (“Aquivo Labs,” “we,” “us,” or “our”) handles personal
        information when you use the Radar service (“Radar”), including the website at radar.aquivolabs.com and the Radar
        application for Shopify merchants. By using Radar, you agree to this policy.
      </p>

      <h2>Who we are</h2>
      <p>
        Radar is owned and operated by {RADAR_LEGAL_ENTITY}, a Romanian company trading as Aquivo Labs. We are
        established and operate in {RADAR_JURISDICTION} and act as the data controller for personal data described in
        this policy. The service is made available to Shopify merchants and their authorized users. If you have questions
        about this policy, contact us through the support channel listed on the Radar listing in the Shopify App Store.
      </p>

      {hasPublicCompanyRegistryDetails() ? (
        <>
          <h3 className="!mt-6 !mb-2 !text-base !font-semibold !text-gray-900">Company registry (Romania)</h3>
          <ul>
            {COMPANY_REGISTERED_OFFICE ? (
              <li>
                <strong>Registered office:</strong> {COMPANY_REGISTERED_OFFICE}
              </li>
            ) : null}
            {COMPANY_CUI ? (
              <li>
                <strong>Unique registration code (CUI):</strong> {COMPANY_CUI}
              </li>
            ) : null}
            {COMPANY_TRADE_REGISTER_ID ? (
              <li>
                <strong>Trade register identification:</strong> {COMPANY_TRADE_REGISTER_ID}
              </li>
            ) : null}
          </ul>
        </>
      ) : null}

      <h2>Romania and data protection</h2>
      <p>
        Because {RADAR_LEGAL_ENTITY} is established in {RADAR_JURISDICTION}, our processing of personal data is
        governed by Regulation (EU) 2016/679 (GDPR) and by {ROMANIA_GDPR_IMPLEMENTING_LAW}, which implements the GDPR in
        Romanian law.
      </p>
      <p>
        If you are located in {RADAR_JURISDICTION}, you may lodge a complaint with the Romanian supervisory authority:
        the {ROMANIA_DPA_FULL_NAME} ({ROMANIA_DPA_SHORT}), at{" "}
        <a href={ROMANIA_DPA_WEBSITE} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
          {ROMANIA_DPA_WEBSITE.replace(/^https:\/\//, "")}
        </a>
        . You may also contact another EU or UK supervisory authority if you are in another country and applicable law
        allows it.
      </p>

      <h2>Information we collect</h2>
      <p>Depending on how you use Radar, we may process:</p>
      <ul>
        <li>
          <strong>Account and authentication data</strong> — such as email address, name, and identifiers provided by
          Shopify or our authentication provider when you sign in or connect a store.
        </li>
        <li>
          <strong>Shop and configuration data</strong> — such as store domain, plan or subscription status, and
          settings you configure in Radar (for example, competitors you choose to track).
        </li>
        <li>
          <strong>Usage and technical data</strong> — such as IP address, device or browser type, approximate location
          derived from IP, timestamps, pages viewed, feature usage, and diagnostic logs needed to operate and secure the
          service.
        </li>
        <li>
          <strong>Competitor and intelligence data</strong> — information about third-party brands, ads, pricing, or
          public storefront signals that you ask us to collect or display, and derived analytics (for example, alerts or
          reports).
        </li>
        <li>
          <strong>Communications</strong> — messages you send to support or that we send for service, security, or (where
          permitted) marketing.
        </li>
      </ul>

      <h2>How we use information</h2>
      <p>We use personal information to:</p>
      <ul>
        <li>Provide, maintain, and improve Radar and its features;</li>
        <li>Authenticate users, connect Shopify stores, and enforce access controls;</li>
        <li>Process subscriptions, billing, and account administration where applicable;</li>
        <li>Send transactional notices, security alerts, and (where you opt in) product updates;</li>
        <li>Analyze usage in aggregate or pseudonymous form to improve performance and reliability;</li>
        <li>Comply with law, respond to lawful requests, and protect our rights and users.</li>
      </ul>

      <h2>Legal bases (Romania, EEA, UK, and similar regions)</h2>
      <p>
        Where the GDPR, {ROMANIA_GDPR_IMPLEMENTING_LAW}, or similar laws apply, we rely on appropriate bases such as:
        performance of a contract (providing Radar); legitimate interests (security, product improvement, fraud
        prevention), balanced against your rights; consent where required (for example certain cookies or marketing); and
        legal obligation where applicable.
      </p>

      <h2>Shopify</h2>
      <p>
        If you install or use Radar through Shopify, Shopify may process personal information according to Shopify’s own
        policies. We receive only the data Shopify shares with us as needed to operate the app. See also our Terms of
        Service regarding your relationship with Shopify.
      </p>

      <h2>Service providers</h2>
      <p>
        We use trusted subprocessors (for example hosting, analytics, email delivery, error monitoring) who process data
        on our instructions and under appropriate agreements. We do not sell your personal information as “sale” is
        defined under applicable U.S. state privacy laws.
      </p>

      <h2>International transfers</h2>
      <p>
        Our primary establishment is in {RADAR_JURISDICTION} (EU). We may also process or store data in countries other
        than your own. Where we transfer personal data from the EEA, UK, or Switzerland to countries not covered by an
        adequacy decision, we use appropriate safeguards such as Standard Contractual Clauses or other mechanisms
        recognized under the GDPR and Romanian law.
      </p>

      <h2>Retention</h2>
      <p>
        We retain personal information only as long as needed for the purposes above, including legal, accounting, and
        security requirements. Retention periods vary by data type and context; we delete or anonymize data when it is no
        longer needed.
      </p>

      <h2>Security</h2>
      <p>
        We implement technical and organizational measures designed to protect personal information. No method of
        transmission or storage is completely secure; we encourage strong passwords and careful handling of account
        access.
      </p>

      <h2>Your rights</h2>
      <p>
        Depending on your location, you may have rights to access, correct, delete, or export your personal information;
        to object to or restrict certain processing; to withdraw consent where processing is based on consent; and to
        lodge a complaint with a supervisory authority (including {ROMANIA_DPA_SHORT} if you are in Romania). Residents
        of the EEA and UK can read more on our{" "}
        <Link to="/gdpr" className="text-primary hover:underline">
          GDPR &amp; EU rights
        </Link>{" "}
        page. To exercise rights, contact us through the support channel on the Radar Shopify App Store listing.
      </p>

      <h2>Cookies and similar technologies</h2>
      <p>
        Our website and app may use cookies, local storage, or similar technologies for session management, preferences,
        analytics, and (where you consent) marketing. You can control some choices through your browser and, where
        shown, our cookie or consent controls.
      </p>

      <h2>Children</h2>
      <p>
        Radar is not directed at children under 16 (the default age under the GDPR in Romania and the EEA for consent to
        information society services, unless your local law sets a lower age between 13 and 15). We do not knowingly
        collect personal information from children.
      </p>

      <h2>Changes</h2>
      <p>
        We may update this Privacy Policy from time to time. We will post the revised policy on this page and update the
        “Last updated” date. Material changes may be communicated through the app or by email where appropriate.
      </p>
    </LegalDocumentPage>
  );
}
